import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, CheckCircle2, Clock, Eye, FileCheck2, MessageSquareWarning } from "lucide-react";
import SecureDocViewer from "@/components/app/SecureDocViewer";
import RequestChangesDialog from "@/components/app/RequestChangesDialog";

const NAVY = "#1B2A4A";
const GOLD = "#C9A44A";
const GREEN = "#047857";

export function TracApprovalChip({ status, testid }) {
  if (status === "Client Approved") {
    return <span data-testid={testid} className="inline-flex items-center gap-1 text-[10px] font-mono-tight uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "#D1FAE5", color: GREEN }}><BadgeCheck size={11} /> Approved</span>;
  }
  if (status === "Changes Requested") {
    return <span data-testid={testid} className="inline-flex items-center gap-1 text-[10px] font-mono-tight uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "#FFEDD5", color: "#B45309" }}><MessageSquareWarning size={11} /> Changes requested</span>;
  }
  if (status === "Awaiting Approval") {
    return <span data-testid={testid} className="inline-flex items-center gap-1 text-[10px] font-mono-tight uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: `${GOLD}26`, color: "#8A6A1E" }}><Clock size={11} /> Awaiting your approval</span>;
  }
  return null;
}

export default function TracApproval({ api, projectId }) {
  const [info, setInfo] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const load = () => api.get(`/client/projects/${projectId}/approval`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  if (!info || info.files.length === 0) return null;
  const approved = info.status === "Client Approved";

  const approve = async () => {
    if (!window.confirm("Approve this project? Your editorial team will be notified.")) return;
    setBusy(true);
    try { await api.post(`/client/projects/${projectId}/approve`); toast.success("Project approved. Thank you!"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Approval failed"); }
    setBusy(false);
  };

  return (
    <div className="mt-4 rounded-2xl bg-white p-4" data-testid="trac-approval-section"
      style={{ border: `2px solid ${approved ? "#10B981" : GOLD}` }}>
      <div className="flex items-center justify-between gap-2">
        <div className="label-caps inline-flex items-center gap-1.5" style={{ color: NAVY }}><FileCheck2 size={12} /> Client Approval</div>
        <TracApprovalChip status={info.status} testid="trac-approval-status" />
      </div>
      <p className="text-xs mt-1.5" style={{ color: `${NAVY}99` }}>Tap a draft to review it in the protected viewer.</p>
      <ul className="mt-3 space-y-2" data-testid="trac-approval-files">
        {info.files.map((f) => (
          <li key={f.id}>
            <button data-testid={`trac-view-draft-${f.id}`} onClick={() => setViewing(f)}
              className="w-full flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left active:scale-[0.99] transition-transform"
              style={{ background: `${NAVY}08`, border: `1px solid ${NAVY}1a` }}>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: NAVY }}>{f.name}</div>
                <div className="text-[11px] font-mono-tight" style={{ color: `${NAVY}80` }}>{f.page_count} page{f.page_count === 1 ? "" : "s"}</div>
              </div>
              <span className="inline-flex items-center gap-1 text-xs font-semibold shrink-0" style={{ color: NAVY }}><Eye size={14} /> View</span>
            </button>
          </li>
        ))}
      </ul>
      {approved ? (
        <div className="mt-3 text-sm font-semibold inline-flex items-center gap-2" style={{ color: GREEN }} data-testid="trac-approved-note">
          <CheckCircle2 size={16} /> Approved on {new Date(info.approved_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
        </div>
      ) : (
        <>
          {info.status === "Changes Requested" && info.last_change_request && (
            <div className="mt-3 rounded-xl px-3 py-2 text-xs" style={{ background: "#FFF7ED", border: "1px solid #FED7AA", color: "#9A3412" }} data-testid="trac-last-change-request">
              <div className="font-semibold">You requested changes on {info.last_change_request.file_name}</div>
              <div className="mt-1 whitespace-pre-wrap">{info.last_change_request.comment}</div>
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button data-testid="trac-request-changes-btn" onClick={() => setChangesOpen(true)}
              className="h-11 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-1.5"
              style={{ background: "white", color: "#B45309", border: "1.5px solid #F59E0B" }}>
              <MessageSquareWarning size={15} /> Request changes
            </button>
            <button data-testid="trac-approve-btn" disabled={busy} onClick={approve}
              className="h-11 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
              style={{ background: NAVY, color: "#F4E9CE", boxShadow: `inset 0 1px 0 ${GOLD}59` }}>
              <CheckCircle2 size={15} /> {busy ? "Approving…" : "Approve"}
            </button>
          </div>
        </>
      )}
      <RequestChangesDialog open={changesOpen} onOpenChange={setChangesOpen} api={api} projectId={projectId} files={info.files} onDone={load} />
      {viewing && <SecureDocViewer client={api} file={viewing} onClose={() => setViewing(null)}
        pageUrl={(n) => `/client/projects/${projectId}/approval-files/${viewing.id}/pages/${n}`} />}
    </div>
  );
}
